import { getPaymentStatusKey } from './appointmentStatusUtils';

const isPaidPaymentStatus = (paymentStatus) => (
  paymentStatus === 2 || paymentStatus === 'Paid'
);

// Same reading as the appointment drawer, whatever form the status arrives in.
const isDepositPaidPaymentStatus = (paymentStatus) => getPaymentStatusKey(paymentStatus) === 'depositPaid';

const toMoney = (value) => Math.max(0, Number(value) || 0);

export const isPendingAppointmentProduct = (item) => {
  const status = item?.status;
  if (status == null || status === '') {
    return true;
  }

  return status === 1 || String(status).toLowerCase() === 'pending';
};

export const getAppointmentProductLines = (appointment) => (
  Array.isArray(appointment?.pendingProducts) ? appointment.pendingProducts : []
);

export const getPendingAppointmentProductLines = (appointment) => (
  getAppointmentProductLines(appointment).filter(isPendingAppointmentProduct)
);

// What completing the appointment sells: the products the customer picked, as they stand.
export const getPendingProductSaleLines = (appointment) => (
  getPendingAppointmentProductLines(appointment).map((item) => ({
    productId: item.productId,
    quantity: item.quantity || 1,
  }))
);

export const getAppointmentProductsTotal = (products) => (
  (products || []).reduce((sum, item) => sum + toMoney(item?.lineTotal), 0)
);

// A service line covered by a membership or a prepaid package has nothing left to collect.
export const isCoveredServiceLine = (service) => Boolean(service?.coveredByMembership || service?.coveredByPackage);

const sumUncoveredServicePrices = (services) => {
  if (!Array.isArray(services)) {
    return null;
  }

  return services.reduce(
    (sum, service) => sum + (isCoveredServiceLine(service) ? 0 : toMoney(service?.unitPrice)),
    0,
  );
};

/**
 * Amount to collect when completing or marking an appointment paid.
 * Additional time-based cost is always due, including fully covered memberships and packages.
 * Covered catalog prices are never collected again.
 */
export function getAppointmentCheckoutBreakdown(appointment) {
  const paid = isPaidPaymentStatus(appointment?.paymentStatus);
  const pending = Boolean(appointment?.pendingMembershipEnrollment);
  const membershipFee = pending ? toMoney(appointment?.membershipPlanPrice) : 0;
  const additionalCost = toMoney(appointment?.additionalCostAmount);
  const hasAmountDue = appointment?.amountDue != null && appointment?.amountDue !== '';
  const uncoveredFromServices = sumUncoveredServicePrices(appointment?.services);

  let extrasDue;
  if (pending) {
    extrasDue = hasAmountDue
      ? Math.max(0, toMoney(appointment.amountDue) - membershipFee)
      : additionalCost;
  } else if (uncoveredFromServices != null) {
    extrasDue = uncoveredFromServices + additionalCost;
  } else if (appointment?.coveredByMembership || appointment?.coveredByServicePackage) {
    extrasDue = additionalCost;
  } else if (hasAmountDue) {
    extrasDue = toMoney(appointment.amountDue);
  } else {
    extrasDue = toMoney(appointment?.totalPrice);
  }

  if (!paid && additionalCost > 0) {
    extrasDue = Math.max(extrasDue, additionalCost);
  }

  // A deposit paid online is already in the store's hands: the till only takes the balance.
  const depositPaid = isDepositPaidPaymentStatus(appointment?.paymentStatus)
    ? toMoney(appointment?.onlinePrepaidAmount)
    : 0;
  extrasDue = Math.max(0, extrasDue - depositPaid);

  // A paid appointment owes nothing more for its services.
  if (paid) {
    extrasDue = 0;
  }

  // Products paid online with the booking are already the store's; only the rest is collected.
  const productsTotal = getAppointmentProductsTotal(getPendingAppointmentProductLines(appointment));
  const productsPaidOnline = paid ? Math.min(productsTotal, toMoney(appointment?.onlineProductAmount)) : 0;
  const productsDue = Math.round((productsTotal - productsPaidOnline) * 100) / 100;

  return {
    paid,
    pending,
    depositPaid,
    membershipFee,
    extrasDue,
    productsDue,
    productsPaidOnline,
    collectableDue: extrasDue + productsDue,
    needsCheckout: productsDue > 0 || (!paid && (extrasDue > 0 || membershipFee > 0)),
  };
}
